/** Poll interval while waiting for Stripe webhook after setup checkout. */
export const ONBOARDING_SETUP_POLL_INTERVAL_MS = 1000;

/** Max polls (~20s) before treating setup as still incomplete. */
export const ONBOARDING_SETUP_POLL_MAX_ATTEMPTS = 20;

export interface OnboardingStatusResult {
  requiresPaymentSetup: boolean;
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Resolves onboarding gate status. When returning from Stripe setup checkout,
 * polls until the webhook marks onboarding complete or attempts are exhausted.
 */
export async function resolveOnboardingGateStatus(
  fetchStatus: () => Promise<OnboardingStatusResult>,
  options?: { pollAfterSetupCheckout?: boolean }
): Promise<{ requiresPaymentSetup: boolean; checkFailed: boolean }> {
  const pollAfterSetupCheckout = options?.pollAfterSetupCheckout ?? false;
  const maxAttempts = pollAfterSetupCheckout ? ONBOARDING_SETUP_POLL_MAX_ATTEMPTS : 1;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const status = await fetchStatus();
      if (!status.requiresPaymentSetup) {
        return { requiresPaymentSetup: false, checkFailed: false };
      }
      if (!pollAfterSetupCheckout || attempt === maxAttempts - 1) {
        return { requiresPaymentSetup: true, checkFailed: false };
      }
    } catch {
      if (!pollAfterSetupCheckout || attempt === maxAttempts - 1) {
        return { requiresPaymentSetup: true, checkFailed: true };
      }
    }

    if (attempt < maxAttempts - 1) {
      await sleep(ONBOARDING_SETUP_POLL_INTERVAL_MS);
    }
  }

  return { requiresPaymentSetup: true, checkFailed: false };
}
